import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VideoInfo = ({ 
  lesson, 
  course, 
  progress, 
  onToggleBookmark, 
  onShareVideo,
  isBookmarked = false,
  lessonPosition = null,
  onNavigatePrevious,
  onNavigateNext,
  hasPrevious = false,
  hasNext = false,
}) => {

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds?.toString()?.padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Data não disponível';
    try {
      return new Date(dateString)?.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return 'Data inválida';
    }
  };

  const currentLessonNumber = lessonPosition?.current ?? 1;
  const totalLessons = lessonPosition?.total ?? 0;

  return (
    <div className="bg-card rounded-lg border border-border p-6 space-y-6">
      {/* Header */}
      <div className="space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h1 className="text-2xl font-heading font-semibold text-foreground mb-2">
              {lesson?.title}
            </h1>
            <p className="text-muted-foreground font-caption">
              {course?.title} • Módulo {lesson?.moduleNumber}
            </p>
          </div>
          
          <div className="flex items-center space-x-2 ml-4">
            <Button
              variant="ghost"
              onClick={onToggleBookmark}
              iconName={isBookmarked ? "BookmarkCheck" : "Bookmark"}
              iconSize={20}
              className="text-muted-foreground hover:text-foreground"
            />
            <Button
              variant="ghost"
              onClick={onShareVideo}
              iconName="Share"
              iconSize={20}
              className="text-muted-foreground hover:text-foreground"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center space-x-6 text-sm text-muted-foreground font-caption">
          <div className="flex items-center space-x-2">
            <Icon name="Clock" size={16} />
            <span>{formatDuration(lesson?.duration)}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Calendar" size={16} />
            <span>{formatDate(lesson?.publishedAt)}</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground font-caption">Progresso da lição</span>
            <span className="text-foreground font-medium">{Math.round(progress)}%</span>
          </div>
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
      {/* Description */}
      {lesson?.description && (
        <div className="space-y-3">
          <h3 className="font-heading font-medium text-foreground">Sobre esta lição</h3>
          <p className="text-muted-foreground leading-relaxed">
            {lesson?.description}
          </p>
        </div>
      )}
      {/* Learning Objectives */}
      {lesson?.objectives && lesson?.objectives?.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-heading font-medium text-foreground">Objetivos de aprendizagem</h3>
          <ul className="space-y-2">
            {lesson?.objectives?.map((objective, index) => (
              <li key={index} className="flex items-start space-x-3">
                <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground">{objective}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* Tags */}
      {lesson?.tags && lesson?.tags?.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-heading font-medium text-foreground">Tags</h3>
          <div className="flex flex-wrap gap-2">
            {lesson?.tags?.map((tag, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-muted text-muted-foreground text-sm rounded-full font-caption"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}
      {/* Instructor */}
      {lesson?.instructor && (
        <div className="space-y-3 pt-4 border-t border-border">
          <h3 className="font-heading font-medium text-foreground">Instrutor</h3>
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center overflow-hidden">
              {lesson?.instructor?.avatar ? (
                <img 
                  src={lesson?.instructor?.avatar} 
                  alt={lesson?.instructor?.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Icon name="User" size={20} color="var(--color-secondary-foreground)" />
              )}
            </div>
            <div>
              <p className="font-medium text-foreground">{lesson?.instructor?.name}</p>
              <p className="text-sm text-muted-foreground font-caption">
                {lesson?.instructor?.title}
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Course Navigation */}
      <div className="pt-4 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground font-caption">
            {lessonPosition
              ? `Lição ${currentLessonNumber} de ${totalLessons}`
              : 'Carregando...'}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronLeft"
              iconPosition="left"
              onClick={onNavigatePrevious}
              disabled={!hasPrevious}
            >
              Anterior
            </Button>
            <Button
              variant="default"
              size="sm"
              iconName="ChevronRight"
              iconPosition="right"
              onClick={onNavigateNext}
              disabled={!hasNext}
            >
              Próxima
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoInfo;
